/**
 * ModuleTreeContext — React Context for Module Management tree + grid state
 * Handles: lazy load, edit, move, delete, grid filters, selection
 */
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { API_BASE as API, getToken} from '../lib/api';

const tkn = () => getToken() || '';
const hdrs = () => ({ Authorization: `Bearer ${tkn()}`, 'Content-Type': 'application/json' });

const ModuleTreeContext = createContext(null);
export const useModuleTree = () => useContext(ModuleTreeContext);

export const ModuleTreeProvider = ({ children }) => {
  // Raw data
  const [modules, setModules] = useState([]);
  const [submodules, setSubmodules] = useState([]);
  const [screens, setScreens] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  // Tree state
  const [expanded, setExpanded] = useState(new Set());
  const [selectedNode, setSelectedNode] = useState(null);
  const [treeSearch, setTreeSearch] = useState('');

  // Grid state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [filters, setFilters] = useState({});
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Load all data
  const loadAll = useCallback(async () => {
    setDataLoading(true);
    try {
      const [mRes, smRes, scRes] = await Promise.all([
        fetch(`${API}/api/uams/modules?page=1&pageSize=200`, { headers: hdrs() }),
        fetch(`${API}/api/uams/submodules?page=1&pageSize=500`, { headers: hdrs() }),
        fetch(`${API}/api/uams/screens?page=1&pageSize=1000`, { headers: hdrs() }),
      ]);
      const [mJ, smJ, scJ] = await Promise.all([mRes.json(), smRes.json(), scRes.json()]);
      if (mJ.success) setModules(mJ.data || []);
      if (smJ.success) setSubmodules(smJ.data || []);
      if (scJ.success) setScreens(scJ.data || []);
    } catch (e) { console.error('ModuleTree loadAll failed', e); }
    setDataLoading(false);
  }, []);

  // Tree actions
  const toggleNode = useCallback((nodeId) => {
    setExpanded(prev => { const n = new Set(prev); if (n.has(nodeId)) n.delete(nodeId); else n.add(nodeId); return n; });
  }, []);

  const selectNode = useCallback((node) => { setSelectedNode(node); setPage(1); }, []);

  const notifySidebar = () => window.dispatchEvent(new Event('modules-changed'));

  // EDIT node — call PUT endpoint
  const editNode = useCallback(async (node, updates) => {
    try {
      let url, body;
      if (node.type === 'module') {
        url = `${API}/api/uams/modules/${node.raw.ModuleId}`;
        body = { ModuleName: updates.name };
      } else if (node.type === 'submodule') {
        url = `${API}/api/uams/submodules/${node.raw.SubModuleId}`;
        body = { SubModuleName: updates.name };
      } else {
        url = `${API}/api/uams/screens/${node.raw.ScreenId}`;
        body = { ScreenName: updates.name, ScreenType: updates.screenType };
      }
      const r = await fetch(url, { method: 'PUT', headers: hdrs(), body: JSON.stringify(body) });
      const j = await r.json();
      if (j.success) { await loadAll(); notifySidebar(); return true; }
      return false;
    } catch { return false; }
  }, [loadAll]);

  // MOVE node — update parent reference
  const moveNode = useCallback(async (node, targetParentId) => {
    try {
      let url, body;
      if (node.type === 'submodule') {
        url = `${API}/api/uams/submodules/${node.raw.SubModuleId}`;
        body = { ParentModuleId: targetParentId };
      } else if (node.type === 'screen') {
        const targetSub = submodules.find(s => s.SubModuleId === targetParentId);
        url = `${API}/api/uams/screens/${node.raw.ScreenId}`;
        body = { SubModuleId: targetParentId, ModuleId: targetSub?.ParentModuleId || node.raw.ModuleId };
      } else return false;
      const r = await fetch(url, { method: 'PUT', headers: hdrs(), body: JSON.stringify(body) });
      const j = await r.json();
      if (j.success) { await loadAll(); notifySidebar(); return true; }
      return false;
    } catch { return false; }
  }, [loadAll, submodules]);

  // PROMOTE: SubModule → Module (with all child screens, placed after AfterModuleId)
  const promoteToModule = useCallback(async (node, afterModuleId) => {
    try {
      const body = { SubModuleId: node.raw.SubModuleId };
      if (afterModuleId !== undefined) body.AfterModuleId = afterModuleId;
      const r = await fetch(`${API}/api/uams/modules/promote`, { method: 'POST', headers: hdrs(), body: JSON.stringify(body) });
      const j = await r.json();
      if (j.success) { await loadAll(); notifySidebar(); return true; }
      return false;
    } catch { return false; }
  }, [loadAll]);

  // DEMOTE: Module → SubModule under target module (with all children)
  const demoteToSubModule = useCallback(async (node, targetModuleId) => {
    try {
      const r = await fetch(`${API}/api/uams/modules/demote`, { method: 'POST', headers: hdrs(), body: JSON.stringify({ ModuleId: node.raw.ModuleId, TargetModuleId: targetModuleId }) });
      const j = await r.json();
      if (j.success) { await loadAll(); notifySidebar(); return true; }
      return false;
    } catch { return false; }
  }, [loadAll]);

  // DELETE node (soft delete)
  const deleteNode = useCallback(async (node) => {
    try {
      let url;
      if (node.type === 'module') url = `${API}/api/uams/modules/${node.raw.ModuleId}`;
      else if (node.type === 'submodule') url = `${API}/api/uams/submodules/${node.raw.SubModuleId}`;
      else url = `${API}/api/uams/screens/${node.raw.ScreenId}`;
      const r = await fetch(url, { method: 'DELETE', headers: hdrs() });
      const j = await r.json();
      if (j.success) { await loadAll(); notifySidebar(); return true; }
      return false;
    } catch { return false; }
  }, [loadAll]);

  // Reset filters
  const resetFilters = useCallback(() => {
    setSearchText(''); setStatusFilter(''); setTypeFilter(''); setPage(1);
  }, []);

  // Lookup maps
  const moduleMap = useMemo(() => Object.fromEntries(modules.map(m => [m.ModuleId, m.ModuleName])), [modules]);
  const submoduleMap = useMemo(() => Object.fromEntries(submodules.map(s => [s.SubModuleId, s.SubModuleName])), [submodules]);
  const screenTypes = useMemo(() => [...new Set(screens.map(s => (s.ScreenType || '').trim()).filter(Boolean))].sort(), [screens]);

  const value = {
    modules, submodules, screens, dataLoading, loadAll,
    expanded, selectedNode, treeSearch, setTreeSearch, toggleNode, selectNode,
    page, setPage, pageSize, setPageSize, sortField, setSortField, sortDir, setSortDir, filters, setFilters,
    searchText, setSearchText, statusFilter, setStatusFilter, typeFilter, setTypeFilter,
    editNode, moveNode, deleteNode, resetFilters, promoteToModule, demoteToSubModule,
    moduleMap, submoduleMap, screenTypes,
  };

  return <ModuleTreeContext.Provider value={value}>{children}</ModuleTreeContext.Provider>;
};
